/* eslint-disable react/prop-types */
import React from 'react';
import Link from 'components/Link';
import { trackEcommerceShopClick } from 'lib/trackEcommerceShopClick';
import { stub as $t } from '@nbcnews/analytics-framework';
import styles from './styles.module.scss';

const starIcons = {
  fullStar: (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="11" viewBox="0 0 12 11" fill="none">
      <path className={styles.stroke} d="M6 0.788302L7.02374 3.84512L7.08034 4.01411L7.25855 4.01572L10.4821 4.04474L7.89125 5.96299L7.74802 6.06903L7.80157 6.23902L8.77009 9.31378L6.14512 7.4425L6 7.33905L5.85488 7.4425L3.22991 9.31378L4.19843 6.23902L4.25198 6.06903L4.10875 5.96299L1.51789 4.04474L4.74145 4.01572L4.91966 4.01411L4.97626 3.84512L6 0.788302Z" stroke="black" strokeWidth="0.5" />
      <path className={styles.fill} d="M6 0.00106812L7.2608 3.76573L11.2308 3.80147L8.04002 6.16391L9.23282 9.95066L6 7.64607L2.76718 9.95066L3.95998 6.16391L0.769189 3.80147L4.7392 3.76573L6 0.00106812Z" fill="black" />
    </svg>
  ),
  halfStar: (
    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
      <path className={styles.stroke} d="M5.5 1.2883L6.52374 4.34512L6.58034 4.51411L6.75855 4.51572L9.98211 4.54474L7.39125 6.46299L7.24802 6.56903L7.30157 6.73902L8.27009 9.81378L5.64512 7.9425L5.5 7.83905L5.35488 7.9425L2.72991 9.81378L3.69843 6.73902L3.75198 6.56903L3.60875 6.46299L1.01789 4.54474L4.24145 4.51572L4.41966 4.51411L4.47626 4.34512L5.5 1.2883Z" stroke="#171717" strokeWidth="0.5" />
      <mask id="mask0_370_1416" maskUnits="userSpaceOnUse" x="0" y="0" width="6" height="12">
        <rect y="0.501068" width="5.5" height="11" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_370_1416)">
        <path className={styles.fill} d="M5.5 0.501068L6.7608 4.26573L10.7308 4.30147L7.54002 6.66391L8.73282 10.4507L5.5 8.14607L2.26718 10.4507L3.45998 6.66391L0.269189 4.30147L4.2392 4.26573L5.5 0.501068Z" fill="#171717" />
      </g>
    </svg>
  ),
  emptyStar: (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="11" viewBox="0 0 12 11" fill="none">
      <path className={styles.stroke} d="M6 0.788302L7.02374 3.84512L7.08034 4.01411L7.25855 4.01572L10.4821 4.04474L7.89125 5.96299L7.74802 6.06903L7.80157 6.23902L8.77009 9.31378L6.14512 7.4425L6 7.33905L5.85488 7.4425L3.22991 9.31378L4.19843 6.23902L4.25198 6.06903L4.10875 5.96299L1.51789 4.04474L4.74145 4.01572L4.91966 4.01411L4.97626 3.84512L6 0.788302Z" stroke="black" strokeWidth="0.5" />
    </svg>
  ),
};

/**
  * Create star rating icons
  * @param {number} rating - Star rating
 */
function createStars(rating) {
  const stars = [];
  // get whole number before decimal, append n full stars
  const fullStarCount = Math.floor(rating);
  for (let i = 0; i < fullStarCount; i += 1) {
    stars.push(starIcons.fullStar);
  }
  // get decimal, if below .5 add empty star, if above add half star
  const halfStarCount = rating - fullStarCount;
  if (halfStarCount > 0) {
    if (halfStarCount >= 0.5) {
      stars.push(starIcons.halfStar);
    } else {
      stars.push(starIcons.emptyStar);
    }
  }
  // append (5-n+1) empty stars to pad out to 5 total
  const emptyStarCount = 5 - Math.ceil(rating);
  if (emptyStarCount > 0) {
    for (let i = 0; i < emptyStarCount; i += 1) {
      stars.push(starIcons.emptyStar);
    }
  }
  return stars;
}

/**
 * Amazon star rating component
 * @param {object} props - Component props
 * @param {string} props.vertical - Vertical
 * @param {object} props.product - Product
 * @param {string} props.size - Size
 */
export const Rating = ({
  vertical, product, size,
}) => {
  const amazonOffer = product.offers.find((offer) => offer?.seller?.name === 'Amazon');

  if (!amazonOffer) {
    return null;
  }

  const reviews = amazonOffer.customerReviews;
  if (!reviews?.count || !reviews?.rating?.value) {
    return null;
  }

  const { count: reviewCount, rating: { value: rating } } = reviews;

  /**
   * Handle click event for ratings link
   */
  const handleRatingsClick = () => {
    trackEcommerceShopClick({ vertical, product });
    $t('track', size === 'large' ? 'mbt_spotlightode_click' : 'mbt_producttease_click', {
      action: 'Amazon Reviews click',
    });
  };

  return (
    <Link
      href={`${amazonOffer.externalUrl}#customerReviews`}
      data-testid="reviews-link"
      className={styles.rating}
      target="_blank"
      rel="sponsored noopener noreferrer"
      onClick={handleRatingsClick}
    >
      <div className={styles.stars}>{createStars(rating)}</div>
      {reviewCount.toLocaleString('en-US')}
      {' '}
Amazon Reviews
    </Link>
  );
};
